import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Track affiliate signup
const trackAffiliateSignup = async (userId, userEmail) => {
    try {
        const refCode = localStorage.getItem('eskrybe_affiliate_ref');
        if (refCode) {
            const api = axios.create({ baseURL: `${API_URL}/api` });
            await api.post(`/affiliate/track/signup?ref_code=${refCode}&user_id=${userId}&user_email=${encodeURIComponent(userEmail)}`);
            console.log('Affiliate signup tracked:', refCode, userId);
            // Keep the ref code for conversion tracking later
        }
    } catch (error) {
        console.error('Failed to track affiliate signup:', error);
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('eskrybe_token'));
    const [loading, setLoading] = useState(true);

    const api = axios.create({
        baseURL: `${API_URL}/api`,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const fetchUser = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        try {
            const response = await api.get('/auth/me');
            setUser(response.data);
        } catch (error) {
            console.error('Failed to fetch user:', error);
            localStorage.removeItem('eskrybe_token');
            setToken(null);
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    const login = async (email, password) => {
        const response = await api.post('/auth/login', { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('eskrybe_token', access_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await api.post('/auth/register', { email, password, name });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('eskrybe_token', access_token);
        setToken(access_token);
        setUser(userData);
        
        // Track affiliate signup if there's a referral code
        await trackAffiliateSignup(userData.id, userData.email);
        
        return userData;
    };

    const logout = () => {
        localStorage.removeItem('eskrybe_token');
        localStorage.removeItem('eskrybe_affiliate_ref'); // Clear affiliate ref on logout
        setToken(null);
        setUser(null);
    };

    const refreshUser = async () => {
        await fetchUser();
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        refreshUser,
        isAuthenticated: !!user,
        // Email verification
        isEmailVerified: user?.email_verified || false,
        // Demo access helpers
        isDemo: user?.is_demo || false,
        isDemoExpired: user?.is_demo && user?.demo_expires_at ? new Date(user.demo_expires_at) < new Date() : false,
        demoExpiresAt: user?.demo_expires_at,
        // Subscription helpers - demo users get full features too
        hasFullAccess: !!user && (
            ['writer', 'author', 'studio'].includes(user.subscription_tier) || 
            user.is_demo
        ),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
